import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, ViewChildren, QueryList, ElementRef, afterNextRender, signal, computed, HostListener, effect } from '@angular/core';
import { MatRippleModule } from '@angular/material/core';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
// import { EyeClosedIconComponent } from '../../icons/card-icon.component';
// import { CartIconComponent } from '../../icons/cart-icon.component';
import { HomeIconComponent } from '../../icons/home-icon.component';
import { OperationsIconComponent } from '../../icons/operations-icon.component';
import { ReceiveIconComponent } from '../../icons/receive-icon.component';
import { MarketIconComponent } from '../../icons/market-icon.component';
import { SiteStore } from 'src/app/store/site.store';
import { UserIconComponent } from "../../icons/user-icon.component";
import { ServiceAccessStore } from 'src/app/store/service-access.store';

@Component({
  selector: 'app-bottomBar',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatRippleModule,
    HomeIconComponent,
    ReceiveIconComponent,
    OperationsIconComponent,
    // EyeClosedIconComponent,
    UserIconComponent,
    MarketIconComponent
],
  templateUrl: './bottomBar.component.html',
  styleUrl: './bottomBar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BottomBarComponent {
  siteStore = inject(SiteStore);
  serviceAccessStore = inject(ServiceAccessStore);
  router = inject(Router);

  /** Indica si la cuenta tiene acceso a al menos un tipo de extracción */
  hasAnyExtractionAccess = computed(() =>
    this.serviceAccessStore.hasAccessToService('wallet_extraction') ||
    this.serviceAccessStore.hasAccessToService('bank_extraction')
  );

  @ViewChildren('navItem', { read: ElementRef }) navItems!: QueryList<ElementRef<HTMLDivElement>>;

  // Signal reactivo para la ruta actual
  currentRoute = signal(this.router.url);

  // Señales para el indicador deslizante
  indicatorStyle = signal({ left: 0, width: 0 });
  
  // Computed para obtener el índice activo basado en la ruta actual
  activeIndex = computed(() => {
    const route = this.currentRoute();
    if (route === '/') return 0;
    if (route.startsWith('/payments')) return 1;
    if (route.startsWith('/market')) return 2;
    if (route.startsWith('/beneficiaries')) return 3;
    if (route.startsWith('/settings')) return 4;
    return -1; // No mostrar indicador si no coincide
  });

  constructor() {
    // Suscribirse a cambios de navegación
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.currentRoute.set(this.router.url);
      });

    // Effect para actualizar la posición cuando cambia el índice activo
    effect(() => {
      this.activeIndex();
      this.hasAnyExtractionAccess();
      setTimeout(() => this.updateIndicatorPosition(), 50);
    });

    // Calcular posición inicial después del primer render
    afterNextRender(() => {
      setTimeout(() => this.updateIndicatorPosition(), 150);
    });
  }

  /**
   * Actualiza la posición y ancho del indicador deslizante
   */
  updateIndicatorPosition() {
    const index = this.activeIndex();

    if (index === -1 || !this.navItems) {
      this.indicatorStyle.set({ left: 0, width: 0 });
      return;
    }

    // El tab "Solicitudes" (índice 1) puede no estar renderizado (*ngIf por
    // acceso a extracciones): el índice de ruta no coincide con el del DOM.
    let domIndex = index;
    if (!this.hasAnyExtractionAccess()) {
      if (index === 1) {
        this.indicatorStyle.set({ left: 0, width: 0 });
        return;
      }
      if (index > 1) domIndex = index - 1;
    }

    const navItemsArray = this.navItems.toArray();

    if (!navItemsArray[domIndex]) {
      this.indicatorStyle.set({ left: 0, width: 0 });
      return;
    }

    const activeElement = navItemsArray[domIndex].nativeElement;
    const left = activeElement.offsetLeft;
    const width = activeElement.offsetWidth;
    
    this.indicatorStyle.set({ left, width });
  }

  /**
   * Recalcular posición del indicador al cambiar tamaño de ventana
   */
  @HostListener('window:resize')
  onWindowResize() {
    setTimeout(() => this.updateIndicatorPosition(), 50);
  }
}
